/* ResourceTable — the descriptor-driven list, on antd.
 *
 * One table for every admin entity in `resources/` (networks, categories, countries, counties,
 * localities, point_groups, points, merchandisers, supervisors, surveys). It renders spec/03
 * archetype A out of the shared parts the redesigned pages already use — `list-page.scss`'s
 * `lp-*` chrome, `EntitySelect`, `FilterChips`, `table-cells`' `StatusIcon` / `RowActions` /
 * `TableFoot` — so a reference list reads as the same product as /visit and /supervisors.
 *
 * Everything still comes from the descriptor: `columns` (with `format` / `sort`), `filters`
 * (entity pickers, `scopeBy` scoping, `defaultRegion` seeding, `type: "me"` toggles), and
 * `softDelete` / `deactivation` for the status glyph.
 *
 * Behaviour kept from the legacy table, deliberately:
 * - **Filter state is remembered per resource in `sessionStorage`** (`rt:<slug>`), so returning
 *   from a record restores the search, filters, sort and page rather than resetting them. That is
 *   why this page does not put its filters in the URL as /visit does: the state is already
 *   restored on the way back, and the two mechanisms would fight over which one wins.
 * - **Sorting is server-side**, over `descriptor.columns[].sort` (or `name` by convention). A
 *   column with no sort key does not sort — `format`-only columns are computed client-side, so
 *   sorting them could only reorder the loaded page.
 * - **The selection is the bulk archive's, and only that.** The descriptor's old `bulkActions`
 *   slot went with the grouping panel: the one action that used it ("add to group") is now a
 *   modal on the bespoke /points list, so a checkbox here means exactly one thing.
 *
 * Two things the archetype asks for and this table does NOT have, both for want of an endpoint:
 * an active/archived `<Segmented>` split and counts on the filters. `GET <endpoint>/` returns no
 * segment counts, and the archived rows come back mixed into the list with a status flag — so the
 * state is shown per row (the leading glyph) rather than as a working-set filter. Adding either
 * is a backend change.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Checkbox, Col, Empty, Flex, Form, Input, Modal, Row, Space, Table, Typography } from "antd";
import { CheckCircleOutlined, ExportOutlined, InboxOutlined, MinusCircleOutlined, SearchOutlined, UndoOutlined } from "@ant-design/icons";

import Alert from "/src/util/alert";

import EntitySelect from "../../component/EntitySelect";
import fieldIcon from "./fields/fieldIcons";
import FilterChips, { useLabelCache } from "../../component/FilterChips";
import { RowActions, StatusIcon, TableFoot, actionsColumn, dash, statusColumn, useTableScrollY } from "../../component/table-cells";
import { DOCUMENTS, ENTITY_DROPDOWN_SETTINGS, PHOTO_REPORT, POINTS_ADMIN, SUPERVISOR_LIST, TRANSLATION_DOWNLOADER, VISIT_PAGE } from "../../config/lang";
import { FILTER_DEBOUNCE_MS } from "../../config/config";
import { fill, regionId, translate } from "../../util/util";
import { deleteItem, fetchList, getDeactivation, isInactive, patchItem } from "./useResource";

const PAGE_SIZES = [25, 50, 100];

/** How many archive/deactivate requests may be in flight at once (there is no bulk endpoint). */
const BULK_CHUNK = 6;

const FIELD_COL = { xs: { flex: "0 0 100%" }, md: { flex: "0 0 50%" }, xl: { flex: "0 0 33.3333%" } };

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);

/** A column sorts only if the descriptor gave it a key; `name` is the standing convention. */
const sortKeyOf = column => column.sort || (column.field === "name" ? "name" : null);

export default function ResourceTable({ descriptor, currentUser, onOpen }) {
  const lang = currentUser.language;
  const t = key => translate(POINTS_ADMIN, key, lang);
  const td = key => translate(ENTITY_DROPDOWN_SETTINGS, key, lang);
  const tp = key => translate(PHOTO_REPORT, key, lang);
  const tv = key => translate(VISIT_PAGE, key, lang);

  const deact = getDeactivation(descriptor);
  const filterDefs = descriptor.filters || [];

  const storeKey = `rt:${descriptor.slug || "resource"}`;

  /* Restored once, on mount. `defaultRegion` filters open pre-scoped to the supervisor's own
     region — but only when there is nothing saved to restore, or reopening a list the user had
     deliberately widened would silently narrow it again. */
  const initial = useRef(
    (() => {
      /* `?q=` is how the app bar's search hands a term over ("see all"). An explicit term outranks
         the saved state, and starts from no filters — a remembered region could hide the very row
         the user just searched for. */
      const handed = new URLSearchParams(window.location.search).get("q");
      if (handed) return { search: handed, filters: {}, page: 1, size: PAGE_SIZES[0], ordering: descriptor.defaultOrdering || "name" };

      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem(storeKey) || "null");
      } catch (_) {
        saved = null;
      }
      if (saved) {
        return {
          search: saved.search || "",
          filters: saved.filters || {},
          page: saved.page || 1,
          size: PAGE_SIZES.includes(saved.size) ? saved.size : PAGE_SIZES[0],
          ordering: saved.ordering ?? descriptor.defaultOrdering ?? "name",
        };
      }
      const region = regionId(currentUser);
      const filters = {};
      if (region) filterDefs.forEach(f => f.defaultRegion && (filters[f.name] = region));
      return { search: "", filters, page: 1, size: PAGE_SIZES[0], ordering: descriptor.defaultOrdering || "name" };
    })()
  ).current;

  const [typed, setTyped] = useState(initial.search);
  const [search, setSearch] = useState(initial.search);
  const [filters, setFilters] = useState(initial.filters);
  const [page, setPage] = useState(initial.page);
  const [size, setSize] = useState(initial.size);
  const [ordering, setOrdering] = useState(initial.ordering);

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  /* `{ id, name }` per selection, kept across pages. */
  const [checked, setChecked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reloads, setReloads] = useState(0);

  const scrollY = useTableScrollY();
  const { labels, remember, nameFor } = useLabelCache();

  /* -------------------------------------------------------------- fetch */

  /* `fetchList` runs on the callback-style `call()`, which has no abort, so the newest request
     wins by sequence number instead — otherwise a slow page-1 lands on top of the page the user
     has since asked for. */
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    setLoading(true);
    fetchList(descriptor, { page, size, search, filters, ordering }, ({ results, count: total }) => {
      if (mine !== seq.current) return;
      setRows(results);
      setCount(total);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptor, page, size, search, ordering, JSON.stringify(filters), reloads]);

  useEffect(() => {
    try {
      sessionStorage.setItem(storeKey, JSON.stringify({ page, size, search, filters, ordering }));
    } catch (_) {
      /* storage unavailable (private mode) — the list still works, it just forgets */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, search, ordering, JSON.stringify(filters)]);

  useEffect(() => {
    if (typed === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(typed);
      setPage(1);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typed, search]);

  const reload = useCallback(() => setReloads(n => n + 1), []);

  /* ------------------------------------------------------------- filters */

  /* Any narrowing invalidates the page number: page 7 of the old result set is usually past the
     end of the new one, which renders an empty table over a non-empty result. */
  const setFilter = (name, value) =>
    setFilters(prev => {
      const next = { ...prev, [name]: value ? idOf(value) : "" };
      /* A dependent filter cannot survive its scope changing — a locality from the old region
         would narrow the list to nothing. */
      filterDefs.forEach(f => f.scopeBy === name && (next[f.name] = ""));
      return next;
    });

  /* Narrowing resets the page — but NOT on mount, or the restored page number would be thrown
     away by the very effect meant to protect the user from an out-of-range page. */
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    setPage(1);
    setChecked([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters), search]);

  const resetFilters = () => {
    setTyped("");
    setSearch("");
    setFilters({});
    setOrdering(descriptor.defaultOrdering || "name");
  };

  const chips = useMemo(() => {
    const out = [];
    if (search) out.push({ key: "__search", label: translate(DOCUMENTS, "search", lang), value: search });
    filterDefs.forEach(f => {
      const value = filters[f.name];
      if (!value) return;
      const label = f.label ? t(f.label) : td(f.entity) || f.name;
      out.push({ key: f.name, label, value: f.type === "me" ? t("filter_created_by_me") : nameFor(f.name, value) });
    });
    return out;
    /* `labels` is a dependency: a chip that arrived as a bare id has to re-render once its name
       lands from `EntitySelect`'s resolution. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, JSON.stringify(filters), labels, lang]);

  const clearChip = key => {
    if (key === "__search") {
      setTyped("");
      setSearch("");
      return;
    }
    setFilter(key, null);
  };

  /* ------------------------------------------------------------- columns */

  const columns = useMemo(() => {
    const out = [];

    /* ⚠ Archived ≠ deactivated (spec/03). A `softDelete` record that is off is ARCHIVED; only a
       `deactivation` record is "inactive". The glyph is shared, the word is not. */
    if (deact)
      out.push(
        statusColumn((value, row) => {
          const off = isInactive(row, deact);
          const label = off ? t(archiving ? "archived" : "inactive") : t("active");
          return <StatusIcon Icon={off ? (archiving ? InboxOutlined : MinusCircleOutlined) : CheckCircleOutlined} tone={off ? "off" : "ok"} label={label} />;
        })
      );

    /* The identity column is the first one that reads a FIELD, not simply the first column: on
       networks that is `name`, while column 0 is the logo — pinning and bolding an <img> would
       both waste the pinned slot and put an element inside a text-measuring `ellipsis`. */
    const identity = descriptor.columns.findIndex(column => column.field);

    descriptor.columns.forEach((column, index) => {
      const key = sortKeyOf(column);
      const isIdentity = index === identity;
      out.push({
        key: column.field || column.label || String(index),
        title: t(column.label) || column.label,
        /* antd's `width` is a minimum, not a cap, so `ellipsis` is what actually keeps a long
           value from stretching the column (spec/02). */
        width: isIdentity ? 280 : column.field ? 180 : 120,
        fixed: isIdentity ? "left" : undefined,
        ellipsis: true,
        sorter: key ? true : undefined,
        sortOrder: key && ordering === key ? "ascend" : key && ordering === `-${key}` ? "descend" : null,
        render: (value, row) => {
          const content = column.format ? column.format(row, lang) : row[column.field];
          if (content === null || content === undefined || content === "" || content === "—") return dash;
          return isIdentity && typeof content === "string" ? (
            <Typography.Text strong ellipsis={{ tooltip: content }}>
              {content}
            </Typography.Text>
          ) : (
            content
          );
        },
      });
    });

    /* The row menu carries the record's lifecycle as well as Open — a menu holding one item that
       repeats the row click is an affordance that does nothing. Which verb it offers follows the
       same `via` split as the detail page and the bulk bar. */
    out.push(
      actionsColumn((value, row) => {
        const off = deact && isInactive(row, deact);
        const items = [{ key: "open", icon: <ExportOutlined />, label: translate(SUPERVISOR_LIST, "open", lang) }];
        if (deact)
          items.push(
            off
              ? { key: "restore", icon: <UndoOutlined />, label: t(archiving ? "restore_btn" : "activate_btn") }
              : {
                  key: "off",
                  icon: archiving ? <InboxOutlined /> : <MinusCircleOutlined />,
                  label: t(archiving ? "archive_btn" : "deactivate_btn"),
                  danger: true,
                }
          );
        return (
          <RowActions
            label={tv("row_actions")}
            items={items}
            onClick={key => {
              if (key === "open") onOpen(row.id);
              else confirmBulk([row], key === "restore");
            }}
          />
        );
      })
    );

    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptor, lang, ordering, deact]);

  /* `+ 60` covers antd's own selection column, which is not in `columns`. */
  const scrollX = useMemo(() => columns.reduce((sum, column) => sum + (column.width || 0), deact ? 60 : 0), [columns, deact]);

  /* antd cycles a sorter asc → desc → none by itself; the empty string is the API's own default
     ordering, which is what "none" means here. */
  const onTableChange = (pagination, tableFilters, sorter) => {
    const column = descriptor.columns.find(c => (c.field || c.label) === sorter?.columnKey);
    const key = column ? sortKeyOf(column) : null;
    const next = sorter?.order && key ? `${sorter.order === "descend" ? "-" : ""}${key}` : "";
    if (next === ordering) return;
    setOrdering(next);
    setPage(1);
  };

  /* Rows are selectable wherever the descriptor can archive or deactivate — the bulk bar does in
     one pass what the record's own detail page can already do one at a time. */
  const rowSelection = deact
    ? {
        fixed: true,
        /* Keys survive paging, so a selection built across two pages archives both. */
        preserveSelectedRowKeys: true,
        selectedRowKeys: checked.map(c => c.id),
        /* antd's own header menu. "All" is all LOADED rows — the bulk bar says so, because the
             alternative reading ("every record matching the filter") would be wrong. */
        selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
        onChange: (keys, selected) => setChecked(keys.map(id => selected.find(row => row && row.id === id) || checked.find(c => c.id === id) || { id })),
      }
    : undefined;

  /* ------------------------------------------------------- bulk archive / deactivate */

  const archiving = !deact || deact.via === "delete";

  /* Restoring is always a PATCH back to the active value, even where archiving is a DELETE —
     `softDelete` is `is_removed`, a flag, so there is nothing to un-delete. Same pair the record's
     own detail page uses (`ResourcePage.runRemove` / `.activate`). */
  const applyOne = (row, restore) =>
    new Promise(resolve => {
      if (restore) patchItem(descriptor, row.id, { [deact.field]: deact.activeValue }, r => resolve(Boolean(r && r.id)));
      else if (deact && deact.via === "patch") patchItem(descriptor, row.id, { [deact.field]: deact.inactiveValue }, r => resolve(Boolean(r && r.id)));
      else deleteItem(descriptor, row.id, ok => resolve(Boolean(ok)));
    });

  /* One request per record — there is no bulk endpoint — but in small batches rather than 100 at
     once, so a full page of selections does not open a hundred sockets. A real bulk endpoint is the
     upgrade if these lists ever grow past a few hundred. */
  const runBulk = async (targets, restore = false) => {
    if (!targets.length) return;
    setBusy(true);
    const done = [];
    for (let i = 0; i < targets.length; i += BULK_CHUNK) {
      const batch = targets.slice(i, i + BULK_CHUNK);
      const results = await Promise.all(batch.map(row => applyOne(row, restore)));
      batch.forEach((row, index) => results[index] && done.push(row));
    }
    setBusy(false);
    if (done.length)
      Alert.success(fill(t(restore ? (archiving ? "restored_n" : "activated_n") : archiving ? "archived_n" : "deactivated_n"), { n: done.length }), {
        position: "top-right",
      });
    if (done.length < targets.length) Alert.warning(fill(t("bulk_failed_n"), { n: targets.length - done.length }), { position: "top-right" });
    setChecked([]);
    /* Refetch rather than patch rows in place: an archived row may leave this page entirely (the
       list is ordered by name, and `?active=true` is not in play here), and the server owns which
       record moves up to fill the gap. */
    reload();
  };

  /* Archiving/deactivating is destructive, so it is confirmed and the confirm says how many
     (spec/03). Restoring is not, so it just runs. */
  const confirmBulk = (targets, restore = false) => {
    if (restore) {
      runBulk(targets, true);
      return;
    }
    Modal.confirm({
      title: fill(t(archiving ? "confirm_archive_n" : "confirm_deactivate_n"), { n: targets.length }),
      /* One record: name it, so the confirm is about a thing rather than a count. */
      content: targets.length === 1 ? targets[0].name || targets[0].title || `#${targets[0].id}` : undefined,
      okText: t(archiving ? "archive_btn" : "deactivate_btn"),
      okButtonProps: { danger: true },
      /* `Modal.confirm` renders outside the tree and does not inherit AppShell's
         `ConfigProvider locale`, so both labels are passed explicitly. */
      cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", lang),
      onOk: () => runBulk(targets, false),
    });
  };

  return (
    <>
      <Card className="lp-filters" variant="outlined">
        {/* `component={false}`: there is nothing to submit — filters apply as they change, and a
            real <form> would turn Enter in the search box into a page reload. */}
        <Form layout="vertical" component={false} colon={false}>
          <Row gutter={[12, 12]}>
            <Col {...FIELD_COL}>
              <Form.Item label={translate(DOCUMENTS, "search", lang)}>
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder={t("search_ph")}
                  aria-label={translate(DOCUMENTS, "search", lang)}
                  value={typed}
                  onChange={event => setTyped(event.target.value)}
                />
              </Form.Item>
            </Col>

            {filterDefs.map(f =>
              f.type === "me" ? (
                <Col {...FIELD_COL} key={f.name}>
                  <Form.Item label=" ">
                    <Checkbox checked={Boolean(filters[f.name])} onChange={event => setFilter(f.name, event.target.checked ? currentUser.id : null)}>
                      {t(f.label)}
                    </Checkbox>
                  </Form.Item>
                </Col>
              ) : (
                <Col {...FIELD_COL} key={f.name}>
                  {/* `|| " "` is load-bearing: `translate()` returns null for a key the group
                      does not carry, and a vertical `<Form.Item>` with no label renders no label
                      row at all — the control then sits a label-height above the labelled search
                      box beside it. A blank spacer keeps the row aligned. */}
                  <Form.Item label={(f.label ? t(f.label) : td(f.entity)) || " "}>
                    <EntitySelect
                      entity={f.entity}
                      /* The same glyph vocabulary the edit form's controls use — `fieldIcon`
                         resolves by name, then entity, and it is the `type` that routes a filter
                         to the entity map. Every other redesigned list carries these. */
                      prefix={fieldIcon({ ...f, type: "entity" })}
                      value={filters[f.name] || ""}
                      seed={f.defaultRegion && currentUser?.county && typeof currentUser.county === "object" ? currentUser.county : null}
                      county={f.scopeBy ? filters[f.scopeBy] : undefined}
                      onChange={option => setFilter(f.name, option)}
                      onResolve={remember(f.name)}
                    />
                  </Form.Item>
                </Col>
              )
            )}
          </Row>

          <FilterChips className="lp-chips" items={chips} closeLabel={tp("a_remove_filter")} onClose={clearChip} />

          <Flex className="lp-filters__foot" align="center" gap="small" wrap>
            <span className="lp-spacer" />
            <Button size="small" onClick={resetFilters}>
              {td("filter_reset")}
            </Button>
          </Flex>
        </Form>
      </Card>

      <Card className="lp-table" variant="outlined">
        <Flex className="lp-table__bar" align="center" gap="small" wrap>
          <Typography.Text strong className="lp-table__title">
            {t(descriptor.titleKey)}
          </Typography.Text>
          <Typography.Text type="secondary" aria-live="polite">
            {tp("records_count")}: <Typography.Text strong>{count}</Typography.Text>
          </Typography.Text>
          <span className="lp-spacer" />
        </Flex>

        <Table
          className="lp-table__grid"
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={rows}
          loading={loading}
          /* Sized from the columns this descriptor actually declares, not a fixed number: a
             two-column reference list would otherwise reserve scroll width it never uses, and a
             wide one would clip. */
          scroll={{ x: scrollX, y: scrollY }}
          rowClassName={row => "lp-row" + (deact && isInactive(row, deact) ? " lp-row--off" : "")}
          rowSelection={rowSelection}
          onRow={row => ({
            onClick: event => {
              /* The selection cell and the row menu own their clicks; everything else opens. */
              if (event.target.closest(".ant-table-selection-column, .ant-dropdown, .ant-btn, .ant-checkbox-wrapper")) return;
              onOpen(row.id);
            },
          })}
          onChange={onTableChange}
          pagination={false}
          /* "Nothing here yet" and "no results for these filters" are different states and get
             different copy (spec/03), each a title plus a line saying what to do about it. */
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Flex vertical gap={4}>
                    <Typography.Text strong>{t(search || chips.length ? "empty_filtered" : "empty")}</Typography.Text>
                    <Typography.Text type="secondary">{t(search || chips.length ? "empty_filtered_desc" : "empty_desc")}</Typography.Text>
                  </Flex>
                }
              />
            ),
          }}
        />

        <TableFoot
          id={descriptor.slug || "rt"}
          page={page}
          size={size}
          total={count}
          sizes={PAGE_SIZES}
          labels={{ perPage: tv("rows_per_page"), range: tv("range_of") }}
          onPage={setPage}
          onSize={next => {
            setSize(next);
            setPage(1);
          }}
        />
      </Card>

      {checked.length > 0 && (
        <Flex className="lp-bulkbar" align="center" gap="middle" wrap>
          <Typography.Text className="lp-bulkbar__n">{fill(tp("w_selected"), { n: checked.length })}</Typography.Text>
          {/* "All" in the header menu means the loaded page, and saying so is cheaper than the
              support ticket from someone who read it as every matching record. */}
          <Typography.Text className="lp-bulkbar__hint">{t("select_page_hint")}</Typography.Text>
          <Space size={8} className="lp-bulkbar__acts">
            <Button size="small" onClick={() => setChecked([])}>
              {tp("clear_selection")}
            </Button>
            {deact && (
              <Button
                size="small"
                danger
                type="primary"
                icon={archiving ? <InboxOutlined /> : <MinusCircleOutlined />}
                loading={busy}
                onClick={() => confirmBulk(checked.slice())}>
                {t(archiving ? "archive_btn" : "deactivate_btn")}
              </Button>
            )}
          </Space>
        </Flex>
      )}
    </>
  );
}
